import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import StockHeatmap, { METRIC_OPTIONS } from './components/StockHeatmap';
import { SECTOR_SHORT } from './lib/sp500';

async function fetchJsonWithFallback(urls) {
  let lastError = null;
  for (const url of urls) {
    try {
      const res = await fetch(url);
      if (!res.ok) {
        lastError = new Error(`HTTP ${res.status}`);
        continue;
      }
      return await res.json();
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError || new Error('Request failed');
}

export default function MarketView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [taxonomyRows, setTaxonomyRows] = useState([]);

  const metric  = searchParams.get('metric')  || '1d';
  const sectors = searchParams.get('sectors') || 'all';
  const subindustry = searchParams.get('subindustry') || 'all';

  const selectedSet = sectors === 'all'
    ? new Set()
    : new Set(sectors.split(',').map(s => s.trim()).filter(Boolean));

  useEffect(() => {
    fetchJsonWithFallback([
      '/api/stonksMarket?includeProfile=1',
      '/.netlify/functions/stonksMarket?includeProfile=1',
    ])
      .then(data => {
        const quotes = data?.quotes || {};
        const rows = Object.values(quotes).map(q => ({
          sector: q?.sector || 'Other',
          sub: q?.industry || 'NA',
        }));
        setTaxonomyRows(rows);
      })
      .catch(() => setTaxonomyRows([]));
  }, []);

  const availableSectors = useMemo(
    () => {
      const set = new Set(taxonomyRows.map(r => r.sector).filter(Boolean));
      set.add('Other');
      return [...set].sort();
    },
    [taxonomyRows]
  );

  const availableSubIndustries = useMemo(
    () => {
      const subs = taxonomyRows
        .filter(r => !selectedSet.size || selectedSet.has(r.sector))
        .map(r => r.sub)
        .filter(Boolean);
      const set = new Set(subs);
      set.add('NA');
      return [...set].sort();
    },
    [taxonomyRows, sectors]
  );

  const setParam = useCallback((key, value) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set(key, value);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  function toggleSector(s) {
    const next = new Set(selectedSet);
    if (next.has(s)) next.delete(s); else next.add(s);
    setParam('sectors', next.size ? [...next].join(',') : 'all');
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* ── Filter bar ─────────────────────────────────────────── */}
      <div className="stonks-filterbar">
        <span className="stonks-filterbar-label">Return</span>
        <div className="stonks-chip-group">
          {METRIC_OPTIONS.map(opt => (
            <button
              key={opt.value}
              className={`stonks-chip${metric === opt.value ? ' active' : ''}`}
              onClick={() => setParam('metric', opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="stonks-topbar-divider" />

        <span className="stonks-filterbar-label">Sector</span>
        <div className="stonks-chip-group" style={{ flexWrap: 'wrap', maxWidth: 540 }}>
          <button
            className={`stonks-chip${!selectedSet.size ? ' active' : ''}`}
            onClick={() => setParam('sectors', 'all')}
          >All</button>
          {availableSectors.map(s => (
            <button
              key={s}
              className={`stonks-chip${selectedSet.has(s) ? ' active' : ''}`}
              onClick={() => toggleSector(s)}
              title={s}
            >
              {SECTOR_SHORT[s] || s}
            </button>
          ))}
        </div>

        <div className="stonks-topbar-divider" />

        <span className="stonks-filterbar-label">Sub-Industry</span>
        <select
          className="stonks-select"
          value={subindustry}
          onChange={e => setParam('subindustry', e.target.value)}
        >
          <option value="all">All Sub-Industries</option>
          {availableSubIndustries.map(sub => (
            <option key={sub} value={sub}>{sub}</option>
          ))}
        </select>

        <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--s-text-muted)' }}>
          S&P 500 · Live data via Yahoo Finance
        </div>
      </div>

      {/* ── Heatmap ──────────────────────────────────────────────── */}
      <StockHeatmap
        mode="market"
        metric={metric}
        sectorFilter={sectors}
        subindustryFilter={subindustry}
      />

      {/* ── Legend ───────────────────────────────────────────────── */}
      <div className="stonks-legend">
        <span className="stonks-filterbar-label">Return</span>
        <div className="stonks-legend-gradient">
          <span>-3%</span>
          <div className="stonks-legend-bar" />
          <span>+3%</span>
        </div>
        <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--s-text-muted)' }}>
          Size = Market Cap · Hover for details
        </span>
      </div>
    </div>
  );
}
